import { useState } from 'react';
import { Navigate, useNavigate } from 'react-router';
import { toast } from 'sonner';
import { Button, Input, Label } from '@staff-app/ui';
import { isEnterKey } from '@/lib/keyboard';
import { useAuthStore } from '@/stores/auth-store';
import {
  auth,
  googleProvider,
  signInWithEmailAndPassword,
  signInWithPopup,
  signOut,
} from '@/lib/firebase';

/**
 * Staff login page.
 *
 * Prod flow: Google Workspace OAuth popup (@optisigns.com hosted domain).
 * Dev flow: optional email+password form gated behind `import.meta.env.DEV`
 *   so local developers can sign in with the shared E2E creds
 *   (support@optisigns.com / 123456y) without opening a Google popup.
 */
export function LoginPage() {
  const navigate = useNavigate();
  const token = useAuthStore((s) => s.token);

  const [googleLoading, setGoogleLoading] = useState(false);
  const [email, setEmail] = useState('support@optisigns.com');
  const [password, setPassword] = useState('');
  const [pwLoading, setPwLoading] = useState(false);

  // Already signed in — bounce back into the app.
  if (token) {
    return <Navigate to="/staff/customers" replace />;
  }

  const enforceOptiSignsDomain = async (credentialEmail: string): Promise<boolean> => {
    if (!credentialEmail.toLowerCase().endsWith('@optisigns.com')) {
      await signOut(auth);
      toast.error('Not authorized', {
        description: 'Only @optisigns.com accounts can access this console.',
      });
      return false;
    }
    return true;
  };

  const handleGoogleSignIn = async () => {
    setGoogleLoading(true);
    try {
      const credential = await signInWithPopup(auth, googleProvider);
      const ok = await enforceOptiSignsDomain(credential.user.email ?? '');
      if (!ok) return;
      navigate('/staff/customers', { replace: true });
    } catch (err) {
      const code = (err as { code?: string })?.code ?? '';
      if (code === 'auth/popup-closed-by-user' || code === 'auth/cancelled-popup-request') {
        return;
      }
      console.error('[LoginPage] Google sign-in failed', err);
      toast.error('Sign in failed', {
        description: (err as { message?: string })?.message ?? 'Unknown error',
      });
    } finally {
      setGoogleLoading(false);
    }
  };

  const handlePasswordSignIn = async () => {
    if (!email.trim() || !password) return;
    setPwLoading(true);
    try {
      const credential = await signInWithEmailAndPassword(auth, email.trim(), password);
      const ok = await enforceOptiSignsDomain(credential.user.email ?? '');
      if (!ok) return;
      navigate('/staff/customers', { replace: true });
    } catch (err) {
      console.error('[LoginPage] Password sign-in failed', err);
      toast.error('Sign in failed', {
        description: (err as { message?: string })?.message ?? 'Unknown error',
      });
    } finally {
      setPwLoading(false);
    }
  };

  // Password login is shown when:
  //   - running in Vite dev mode (localhost:5174), OR
  //   - VITE_ENABLE_PASSWORD_LOGIN is explicitly "true" at build time
  //     (used for sandbox Cloudflare Pages builds so the team can sign in
  //     without Google OAuth popup / domain-allowlist round-trips).
  const devMode =
    import.meta.env.DEV ||
    String(import.meta.env.VITE_ENABLE_PASSWORD_LOGIN ?? '').toLowerCase() === 'true';

  return (
    <div
      className="flex min-h-screen items-center justify-center bg-muted px-4"
      data-testid="staff-login-page"
    >
      <div className="w-full max-w-sm rounded-lg border bg-card p-8 shadow-sm">
        <h1 className="text-2xl font-bold text-foreground">{'OptiSigns Staff'}</h1>
        <p className="mt-2 text-sm text-muted-foreground">
          {'Sign in with your @optisigns.com Google account.'}
        </p>
        <Button
          className="mt-6 w-full"
          onClick={handleGoogleSignIn}
          disabled={googleLoading || pwLoading}
          data-testid="staff-login-google-button"
        >
          {googleLoading ? 'Signing in…' : 'Sign in with Google'}
        </Button>

        {devMode && (
          <div className="mt-6 border-t pt-6" data-testid="staff-login-password-section">
            <p className="mb-3 text-xs font-medium uppercase tracking-wide text-muted-foreground">
              {'Or sign in with password (dev only)'}
            </p>
            <div className="space-y-3">
              <div className="space-y-2">
                <Label htmlFor="staff-login-email">{'Email'}</Label>
                <Input
                  id="staff-login-email"
                  type="email"
                  autoComplete="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  data-testid="staff-login-password-email"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="staff-login-password">{'Password'}</Label>
                <Input
                  id="staff-login-password"
                  type="password"
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  onKeyDown={(e) => {
                    if (isEnterKey(e) && !pwLoading) void handlePasswordSignIn();
                  }}
                  data-testid="staff-login-password-input"
                />
              </div>
              <Button
                variant="outline"
                className="w-full"
                onClick={handlePasswordSignIn}
                disabled={pwLoading || googleLoading || !email.trim() || !password}
                data-testid="staff-login-password-button"
              >
                {pwLoading ? 'Signing in…' : 'Sign in with password'}
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
